import { useState } from "react";
import { Card, Pill, SectionLabel } from "./ui";
import { Ic } from "./icons";
import {
  plans as pricingPlans,
  priceFor,
  formatEur,
  resolveStripePriceId,
  type BillingCycle,
  type PlanId,
} from "../data/pricing";
import { openBillingPortal, PortalError } from "../lib/checkout";

/**
 * Section "Changer de plan" sur Settings → Abonnement.
 *
 * Affiche les 4 plans en grille avec le plan actuel mis en évidence (ring
 * lime + badge "Plan actuel"). Click sur un autre plan → ouvre le Stripe
 * Customer Portal pré-rempli sur l'écran de confirmation du switch (Stripe
 * gère le prorata + la confirmation, le webhook re-sync notre billing).
 *
 * Sous la grille, un lien discret pour le changement de cycle (mensuel ↔
 * annuel) sur le **même plan** — séparé pour ne pas surcharger une matrice
 * 4×2.
 *
 * Préalable : "Allow customers to switch plans" activé dans Stripe Dashboard
 * → Settings → Billing → Customer portal. Sinon le call retourne
 * `invalid_request_error` quand on essaie de créer la session avec
 * `flow_data.subscription_update_confirm`.
 */
export function PlanSwitcher({
  currentPlanId,
  currentCycle,
  cancelling,
}: {
  currentPlanId: PlanId;
  currentCycle: BillingCycle;
  /** Quand l'user a une résiliation programmée (cancel_at_period_end), on
   *  masque le switcher : laisser changer de plan sur une sub qui s'arrête
   *  est confus. Il doit d'abord cliquer "Reprendre l'abonnement". */
  cancelling: boolean;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (cancelling) return null;

  async function handleSwitch(planId: PlanId, cycle: BillingCycle) {
    const priceId = resolveStripePriceId(planId, cycle);
    if (!priceId) {
      setError(
        "Configuration tarifaire incomplète — contactez le support.",
      );
      return;
    }
    setError(null);
    setBusy(`${planId}:${cycle}`);
    try {
      await openBillingPortal({ switchToPriceId: priceId });
    } catch (e) {
      if (e instanceof PortalError) {
        if (e.code === "no-subscription") {
          setError(
            "Aucun abonnement actif détecté. Réessayez dans quelques secondes.",
          );
        } else if (e.code === "switch-to-current-plan") {
          setError("Vous êtes déjà sur ce plan.");
        } else {
          setError(e.message);
        }
      } else {
        setError(
          e instanceof Error ? e.message : "Impossible d'ouvrir le portail.",
        );
      }
      setBusy(null);
    }
    // Sur succès, openBillingPortal redirige déjà la page.
  }

  // Toggle de cycle : same plan, autre cycle. Construit un "switch" vers le
  // plan courant mais dans l'autre cycle. Stripe portal détecte que c'est
  // un changement de price (interval different) et applique son prorata.
  const otherCycle: BillingCycle =
    currentCycle === "engagement" ? "monthly" : "engagement";
  const cycleSwitchLabel =
    currentCycle === "monthly"
      ? "Passer à l'engagement annuel — économisez 20 %"
      : "Repasser en mensuel sans engagement";

  return (
    <Card className="p-6">
      <SectionLabel>Changer de plan</SectionLabel>
      <p className="mt-2 text-[13px] text-ink-600 leading-relaxed max-w-[640px]">
        Le changement passe par notre partenaire de paiement Stripe. Le
        prorata est calculé automatiquement — vous ne payez que la
        différence pour le temps restant du cycle en cours.
      </p>
      {error && (
        <div className="mt-3 text-[12.5px] text-rose-700 font-medium">
          {error}
        </div>
      )}

      <div
        className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3"
        role="list"
        aria-label="Plans disponibles"
      >
        {pricingPlans.map((p) => {
          const isCurrent = p.id === currentPlanId;
          const price = priceFor(p, currentCycle);
          const busyKey = `${p.id}:${currentCycle}`;
          const isBusy = busy === busyKey;
          const ctaLabel = (() => {
            if (isCurrent) return "Plan actuel";
            const currentPlan = pricingPlans.find(
              (pl) => pl.id === currentPlanId,
            );
            const targetQuota = p.ficheQuota;
            const currentQuota = currentPlan?.ficheQuota ?? 0;
            if (targetQuota > currentQuota) return "Upgrade";
            return "Choisir";
          })();
          return (
            <div
              key={p.id}
              role="listitem"
              className={`rounded-xl2 p-4 flex flex-col transition ${
                isCurrent
                  ? "bg-paper hairline ring-2 ring-ink-900"
                  : "bg-ink-50"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-[13.5px] font-semibold text-ink-900">
                  {p.name}
                </div>
                {isCurrent && (
                  <Pill tone="lime" icon={<Ic.Check size={10} />}>
                    Plan actuel
                  </Pill>
                )}
              </div>
              <div className="mt-1.5 flex items-baseline gap-1">
                <span className="text-[22px] leading-none font-semibold tabular tracking-tight">
                  {formatEur(price)}&nbsp;€
                </span>
                <span className="text-[10.5px] text-ink-500">/ mois</span>
              </div>
              <div className="mt-0.5 text-[11.5px] text-ink-500">
                {p.ficheQuota} fiches / mois
              </div>

              <button
                type="button"
                onClick={() => handleSwitch(p.id, currentCycle)}
                disabled={isCurrent || isBusy}
                className={`mt-4 w-full inline-flex items-center justify-center gap-1 h-9 rounded-md text-[12.5px] font-medium transition ${
                  isCurrent
                    ? "bg-transparent text-ink-400 cursor-default"
                    : "bg-paper hairline text-ink-900 hover:bg-ink-900 hover:text-white"
                }`}
              >
                {isBusy ? "Ouverture…" : ctaLabel}
                {!isCurrent && !isBusy && <Ic.Arrow size={11} />}
              </button>
            </div>
          );
        })}
      </div>

      {/* Cycle toggle : passer mensuel ↔ annuel sur le même plan. Lien
          discret en bas pour ne pas dominer la grille des plans. */}
      <button
        type="button"
        onClick={() => handleSwitch(currentPlanId, otherCycle)}
        disabled={busy === `${currentPlanId}:${otherCycle}`}
        className="mt-4 text-[12px] text-ink-500 hover:text-ink-900 underline decoration-ink-300 underline-offset-2 hover:decoration-ink-900 transition disabled:opacity-50"
      >
        {busy === `${currentPlanId}:${otherCycle}`
          ? "Ouverture…"
          : `${cycleSwitchLabel} →`}
      </button>
    </Card>
  );
}
